import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { ButtonProps } from "@braintrust/braintrust-ui-components";
import { Box, Button } from "@braintrust/braintrust-ui-components";
import { unauthenticatedUserJobApplyClicked } from "@js/apps/jobs/actions";
import { RouterLink } from "@js/components/link";
import { useAppDispatch } from "@js/hooks";
import type { Job } from "@js/types/jobs";
import { assertUnreachable } from "@js/utils";

import { addViewedPublicJob } from "../../apps/public-listing/helpers/viewed-public-jobs";

type NotLoggedInApplyButtonProps = {
  job: { id: number; job_status?: Job["job_status"] };
  fullWidth?: boolean;
  size?: ButtonProps["size"];
  className?: string;
};

export const NotLoggedInApplyButton = ({
  className,
  job,
  fullWidth,
  ...props
}: NotLoggedInApplyButtonProps) => {
  const jobId = job.id;
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();

  const canApply = getCanApply(job.job_status);

  const isApplyButton = canApply;
  const label = useMemo(() => {
    if (!canApply) {
      return "Sign up to see other opportunities";
    }

    return "Apply";
  }, [canApply]);

  const onClick = useCallback(() => {
    addViewedPublicJob(jobId);
    dispatch(unauthenticatedUserJobApplyClicked(jobId));
  }, [dispatch, jobId]);

  const navigateTo = useMemo(() => {
    const paramsString = params.toString();

    if (isApplyButton) {
      const search = paramsString ? `?${paramsString}` : "";
      return `/jobs/${jobId}/proposals/new/${search}`;
    }

    const search = paramsString ? `&${paramsString}` : "";
    return `/auth/sign_up/?job_id=${jobId}${search}`;
  }, [params, isApplyButton, jobId]);

  return (
    <Box
      sx={{
        width: fullWidth ? "100%" : "initial",
        whiteSpace: "nowrap",
      }}
    >
      <Button
        className={className}
        variant="positive-2"
        shape="squared"
        to={navigateTo}
        onClick={onClick}
        RouterLink={RouterLink}
        fullWidth={fullWidth}
        {...props}
      >
        {label}
      </Button>
    </Box>
  );
};

export const getCanApply = (
  status?: EnumType<typeof ENUMS.JobStatus>,
): boolean => {
  if (!status) return false;

  switch (status) {
    case ENUMS.JobStatus.ON_HOLD:
    case ENUMS.JobStatus.COMPLETED_HIRED:
    case ENUMS.JobStatus.COMPLETED_NOT_HIRED:
    case ENUMS.JobStatus.COMPLETED:
    case ENUMS.JobStatus.ACTIVE_FILLED:
      return false;
    case ENUMS.JobStatus.ACTIVE:
    case ENUMS.JobStatus.ACTIVE_NOT_FILLED:
    case ENUMS.JobStatus.OPEN:
      return true;
    default:
      assertUnreachable(status);
      return true;
  }
};
